import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useSetFilters from '@/hooks/useSetFilters';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import { useToastCtx } from '@/context/ToastContext/ToastContext';
import { selectWalletDisplayMode } from '@/state/clientStorage/clientStorageSelectors';
import { setWalletDisplayMode } from '@/state/clientStorage/clientStorageSlice';
import { WALLET_DISPLAY_MODE } from '@/state/clientStorage/clientStorageTypes';

const useWalletDisplayMode = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'profile_menu_drawer' });
    const dispatch = useDispatch();
    const walletDisplayMode = useSelector(selectWalletDisplayMode);
    const isMemberAuthorized = useIsMemberAuthorized();
    const { uploadFilters } = useSetFilters();
    const { toggleToast } = useToastCtx();

    const changeWalletDisplayMode = useCallback(
        async (mode: WALLET_DISPLAY_MODE) => {
            if (!isMemberAuthorized || mode === walletDisplayMode) return;

            const previousMode = walletDisplayMode;
            dispatch(setWalletDisplayMode(mode));
            // HTTP failures are reported to Sentry by the Axios interceptor
            const isSaved = await uploadFilters({ walletDisplayMode: mode });

            if (!isSaved) {
                dispatch(setWalletDisplayMode(previousMode));
                toggleToast(true, {
                    label: t('wallet_display_save_error'),
                    severity: 'error',
                    icon: 'error',
                });
            }
        },
        [walletDisplayMode, isMemberAuthorized, dispatch, uploadFilters, toggleToast, t],
    );

    return { walletDisplayMode, changeWalletDisplayMode };
};

export default useWalletDisplayMode;
